export default function HostPilotSupport() {
  const faqs = [
    {
      q: "How do I start my first server?",
      a: "Open HostPilot, add a site (create a starter site or import your own files), then tap Start on the dashboard. The app shows the address (for example http://192.168.1.7:8080) the moment the server is running. Open that address in a browser on any device connected to the same Wi-Fi network — or tap the QR button and scan it with the other device's camera.",
    },
    {
      q: "How do I keep the server running in the background?",
      a: "This is HostPilot's core feature. The server runs inside a foreground service with a persistent notification showing live status and uptime. For maximum reliability, complete the Reliability Checklist on the dashboard: allow notifications and grant the battery-optimization exemption when prompted. Once both checks are green, the server keeps serving with the screen off and the app in the background.",
    },
    {
      q: "Another device can't reach my server. What should I check?",
      a: "1) Both devices must be on the same network (same Wi-Fi). 2) Make sure the server is set to LAN mode, not loopback-only. 3) Some routers and guest/hotel networks enable 'AP isolation', which blocks devices from talking to each other — try a different network or your phone's own hotspot. 4) Confirm you typed the address exactly as shown, including the port.",
    },
    {
      q: "What can I serve?",
      a: "Any static content: HTML, CSS, JavaScript, images, audio, video, PDFs, downloads — anything that lives in files. index.html is served automatically for folder addresses, video and audio support streaming, and you can toggle a folder-listing page for quick file sharing. Server-side languages (like PHP) are not part of this version.",
    },
    {
      q: "Does it work without internet?",
      a: "Yes. The server runs entirely on your local network — no internet connection is required to serve files. An internet connection is only used for optional things like ads and diagnostics.",
    },
    {
      q: "Is my server visible on the internet?",
      a: "No. HostPilot binds only to your device's local network addresses. Devices on the internet cannot reach it unless you deliberately configure your own network to forward traffic. On shared networks, anyone on that network can reach a LAN-mode server while it runs — use loopback mode for private work, and password protection (Pro) when you need access control.",
    },
    {
      q: "What is the ad model?",
      a: "The app is free to use with ads. A Pro upgrade removes ads and unlocks power features. Pro is available as a monthly or annual subscription, or as a one-time lifetime purchase. Subscriptions are managed through Google Play and can be cancelled at any time.",
    },
    {
      q: "What does Pro unlock?",
      a: "Pro removes all ads and unlocks: custom port selection, loopback/LAN access mode control, password protection (basic auth), uploading files to your server from a browser, and multiple sites. The reliability features — background serving, the persistent notification, and the checklist — are free for everyone.",
    },
    {
      q: "The app says the port is in use.",
      a: "Another app on your device is already using that port. Stop the other app, or (with Pro) pick a different port between 1024 and 65535 in Settings.",
    },
    {
      q: "The app is crashing or misbehaving.",
      a: "First, make sure you are on the latest version from Google Play. Then try stopping and restarting the server, and restarting the app. If the problem persists, email us with your device model, Android version, and what you were doing — crash reports help us fix issues quickly.",
    },
  ];

  return (
    <main className="min-h-screen bg-[#071118] text-slate-300">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-10 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-emerald-400">
            HostPilot Support
          </h1>
          <p className="mt-3 text-slate-400">
            Run a web server on your Android device — and keep it running.
          </p>
        </header>

        <div className="mb-10 rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <h2 className="text-xl font-bold text-slate-100">Contact us</h2>
          <p className="mt-2">
            Questions, bug reports, feature requests:{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-cyan-400 underline"
            >
              trionapps@gmail.com
            </a>
          </p>
          <p className="mt-1 text-sm text-slate-500">
            We usually reply within 1-2 business days.
          </p>
        </div>

        <h2 className="mb-6 text-2xl font-bold text-emerald-400">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div
              key={f.q}
              className="rounded-xl border border-slate-800 bg-slate-900/40 p-6"
            >
              <h3 className="font-bold text-slate-100">{f.q}</h3>
              <p className="mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <h2 className="text-xl font-bold text-slate-100">Privacy</h2>
          <p className="mt-2">
            Your files are served from your device and never uploaded to us.
            Read the full{" "}
            <a
              href="/apps/hostpilot/privacy-policy"
              className="text-cyan-400 underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-slate-800 pt-4 text-sm text-slate-500">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
